import { useEffect, useState } from 'react';
import { settingsAPI } from '../lib/api';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Globe,
  MessageCircle,
  ShieldCheck,
  CreditCard,
  Award,
} from 'lucide-react';

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

// DESTACK official brand palette (from official logo)
// Royal blue #0047AB predominant · amber-400/yellow-300 premium accents · white
const BRAND = {
  gradient:
    'linear-gradient(135deg, #052466 0%, #0A3FA0 40%, #0047AB 55%, #0A3FA0 70%, #041a4d 100%)',
  goldLine:
    'linear-gradient(90deg, transparent 0%, #FCD34D 15%, #FBBF24 50%, #FCD34D 85%, transparent 100%)',
};

const SectionTitle = ({ children, testId }) => (
  <div className="mb-4">
    <h4
      className="text-[11px] font-bold tracking-[0.35em] uppercase text-amber-300"
      data-testid={testId}
    >
      {children}
    </h4>
    <div className="mt-2 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300" />
  </div>
);

export const SiteFooter = () => {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let active = true;
    settingsAPI
      .getFooter()
      .then(({ data }) => {
        if (active) setCfg(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!cfg) {
    return <div className="h-32" style={{ background: BRAND.gradient }} aria-hidden="true" />;
  }

  const year = new Date().getFullYear();
  const cityState = [cfg.city, cfg.state].filter(Boolean).join(' / ');
  const addressLine = [cfg.address, cityState, cfg.cep].filter(Boolean).join(' • ');
  const wa = onlyDigits(cfg.whatsapp);
  const tel = onlyDigits(cfg.phone);
  const instaUser = (cfg.instagram || '')
    .replace(/^@/, '')
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
    .replace(/\/$/, '');
  const hasContact = !!(cfg.address || cfg.city || cfg.state || cfg.cep || cfg.phone || cfg.whatsapp || cfg.email);
  const hasSocial = !!(cfg.instagram || cfg.facebook || cfg.website);
  const columnCount = 1 + (hasContact ? 1 : 0) + (hasSocial ? 1 : 0);
  const gridCols =
    columnCount === 3 ? 'md:grid-cols-3' : columnCount === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';
  const copyrightText =
    cfg.copyright_text?.trim() ||
    `${cfg.company_name || 'DESTACK VENDAS MAIS'} — Todos os direitos reservados`;

  return (
    <footer
      className="relative text-white mt-12 overflow-hidden"
      style={{ background: BRAND.gradient }}
      data-testid="site-footer"
    >
      {/* Subtle dot pattern texture */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
          backgroundSize: '18px 18px',
        }}
      />

      {/* Golden shimmer top line */}
      <div
        className="relative h-[3px] shadow-[0_1px_10px_rgba(252,211,77,0.55)]"
        style={{ background: BRAND.goldLine }}
      />

      {/* Trust strip */}
      {cfg.show_payment_info && cfg.payment_info_text && (
        <div className="relative border-b border-amber-300/30 bg-white/[0.06] backdrop-blur">
          <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
            <div className="inline-flex items-center gap-2 text-white">
              <span className="w-8 h-8 rounded-full bg-amber-400/15 border border-amber-300/50 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-amber-300" />
              </span>
              <span className="font-semibold tracking-wide">Marketplace verificado</span>
            </div>
            <span className="hidden sm:block w-1 h-1 rounded-full bg-amber-300/70" />
            <div className="inline-flex items-center gap-2 text-white">
              <span className="w-8 h-8 rounded-full bg-amber-400/15 border border-amber-300/50 flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-amber-300" />
              </span>
              <span>{cfg.payment_info_text}</span>
            </div>
            <span className="hidden sm:block w-1 h-1 rounded-full bg-amber-300/70" />
            <div className="inline-flex items-center gap-2 text-white">
              <span className="w-8 h-8 rounded-full bg-amber-400/15 border border-amber-300/50 flex items-center justify-center">
                <Award className="w-4 h-4 text-amber-300" />
              </span>
              <span>Compra 100% segura</span>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="relative max-w-6xl mx-auto px-6 py-14 pb-24 sm:pb-14">
        <div className={`grid grid-cols-1 ${gridCols} gap-10 lg:gap-14`}>
          {/* Brand / Institutional */}
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              {cfg.logo_url ? (
                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl bg-amber-400/25 blur-xl" aria-hidden="true" />
                  <div className="relative rounded-2xl bg-white/95 p-2 shadow-lg ring-2 ring-amber-300/60">
                    <img
                      src={optimizeCloudinaryUrl(cfg.logo_url, { width: 200 })}
                      alt={cfg.company_name}
                      loading="lazy"
                      className="h-14 w-14 object-contain"
                      data-testid="footer-logo"
                    />
                  </div>
                </div>
              ) : (
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg ring-2 ring-amber-300/60"
                  style={{ background: 'linear-gradient(135deg,#ffffff 0%,#fef3c7 100%)' }}
                  data-testid="footer-logo-fallback"
                >
                  <span className="text-blue-800 font-extrabold text-2xl tracking-tight">D</span>
                </div>
              )}
              <div>
                <h3 className="text-xl font-extrabold tracking-tight text-white leading-none" data-testid="footer-company-name">
                  DESTACK Venda Mais
                </h3>
                <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300 mt-1.5">
                  Marketplace Inteligente
                </p>
                {cfg.company_tagline && (
                  <p className="text-xs text-blue-100/90 mt-2 max-w-xs" data-testid="footer-tagline">
                    {cfg.company_tagline}
                  </p>
                )}
              </div>
            </div>

            {cfg.legal_name && (
              <p className="text-[11px] tracking-wide text-blue-100/70 uppercase" data-testid="footer-legal-name">
                {cfg.legal_name}
              </p>
            )}
            {cfg.institutional_text && (
              <p className="text-sm text-white/85 leading-relaxed" data-testid="footer-institutional">
                {cfg.institutional_text}
              </p>
            )}
            {(cfg.cnpj || cfg.tax_id) && (
              <div className="pt-4 mt-4 border-t border-amber-300/25 space-y-1">
                {cfg.cnpj && (
                  <p className="text-xs text-blue-100/80" data-testid="footer-cnpj">
                    CNPJ: <span className="text-white font-medium">{cfg.cnpj}</span>
                  </p>
                )}
                {cfg.tax_id && (
                  <p className="text-xs text-blue-100/80" data-testid="footer-tax-id">
                    IE: <span className="text-white font-medium">{cfg.tax_id}</span>
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Contact */}
          {hasContact && (
            <div>
              <SectionTitle testId="footer-title-contact">Contato</SectionTitle>
              <ul className="space-y-4 text-sm">
                {(cfg.address || cfg.city || cfg.state || cfg.cep) && (
                  <li className="flex gap-3" data-testid="footer-address">
                    <span className="w-8 h-8 rounded-lg bg-white/10 border border-amber-300/40 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-amber-300" />
                    </span>
                    <span className="text-white/90 leading-relaxed pt-1">{addressLine}</span>
                  </li>
                )}
                {cfg.phone && (
                  <li className="flex gap-3 items-center" data-testid="footer-phone">
                    <span className="w-8 h-8 rounded-lg bg-white/10 border border-amber-300/40 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-amber-300" />
                    </span>
                    <a href={`tel:+55${tel}`} className="text-white/90 hover:text-amber-300 transition-colors">
                      {cfg.phone}
                    </a>
                  </li>
                )}
                {cfg.whatsapp && (
                  <li className="flex gap-3 items-center" data-testid="footer-whatsapp">
                    <span className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-300/50 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-4 h-4 text-emerald-300" />
                    </span>
                    <a
                      href={`https://wa.me/55${wa}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/90 hover:text-emerald-200 transition-colors"
                    >
                      {cfg.whatsapp}
                    </a>
                  </li>
                )}
                {cfg.email && (
                  <li className="flex gap-3 items-center" data-testid="footer-email">
                    <span className="w-8 h-8 rounded-lg bg-white/10 border border-amber-300/40 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-amber-300" />
                    </span>
                    <a href={`mailto:${cfg.email}`} className="text-white/90 hover:text-amber-300 transition-colors break-all">
                      {cfg.email}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Social / Links */}
          {hasSocial && (
            <div>
              <SectionTitle testId="footer-title-social">Conecte-se</SectionTitle>
              <ul className="space-y-4 text-sm">
                {cfg.instagram && (
                  <li>
                    <a
                      href={`https://instagram.com/${instaUser}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-3 text-white/90 hover:text-white transition-colors"
                      data-testid="footer-instagram"
                    >
                      <span className="w-10 h-10 rounded-xl bg-gradient-to-tr from-pink-500 via-rose-500 to-amber-400 flex items-center justify-center shadow-lg ring-1 ring-white/20 group-hover:scale-105 transition-transform">
                        <Instagram className="w-4 h-4 text-white" />
                      </span>
                      <span className="font-medium">@{instaUser}</span>
                    </a>
                  </li>
                )}
                {cfg.facebook && (
                  <li>
                    <a
                      href={cfg.facebook.startsWith('http') ? cfg.facebook : `https://facebook.com/${cfg.facebook}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-3 text-white/90 hover:text-white transition-colors"
                      data-testid="footer-facebook"
                    >
                      <span className="w-10 h-10 rounded-xl bg-blue-800 flex items-center justify-center shadow-lg ring-1 ring-amber-300/40 group-hover:scale-105 transition-transform">
                        <Facebook className="w-4 h-4 text-white" />
                      </span>
                      <span className="font-medium">Facebook</span>
                    </a>
                  </li>
                )}
                {cfg.website && (
                  <li>
                    <a
                      href={cfg.website.startsWith('http') ? cfg.website : `https://${cfg.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-3 text-white/90 hover:text-white transition-colors"
                      data-testid="footer-website"
                    >
                      <span className="w-10 h-10 rounded-xl bg-white/10 border border-amber-300/50 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                        <Globe className="w-4 h-4 text-amber-300" />
                      </span>
                      <span className="font-medium">{cfg.website.replace(/^https?:\/\//, '')}</span>
                    </a>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Golden divider before bottom bar */}
        <div className="mt-14 h-[2px] rounded-full" style={{ background: BRAND.goldLine }} />

        {/* Bottom bar */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/80 tracking-wide" data-testid="footer-copyright">
            &copy; {year} {copyrightText}
          </p>
          <div className="flex items-center gap-2 text-xs text-white/80">
            <span>Desenvolvido por</span>
            <span
              className="font-bold px-2.5 py-0.5 rounded-full text-blue-900"
              style={{
                background: 'linear-gradient(90deg,#FCD34D 0%,#FBBF24 100%)',
                boxShadow: '0 2px 8px rgba(251,191,36,0.35)',
              }}
            >
              DESTACK
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
